import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeneral } from '../../contexts';
import Services from '../../services/Services';
import { Notification } from '../../types/notification.types';
import { generateEventUrl, generateCommunityShareUrl } from '../../utils/urlSlug';
import ChannelInitialIcon from '../ui/ChannelInitialIcon/ChannelInitialIcon';
import LoadingSkeleton from '../ui/LoadingSkeleton/LoadingSkeleton';
import GrayBanner from '../ui/GrayBanner/GrayBanner';
import styles from './NotificationsDropdown.module.css';

interface NotificationsDropdownProps {
  onClose: () => void;
  dropdownRef?: React.RefObject<HTMLDivElement>;
  onUnreadCountChange?: (count: number) => void;
}

interface FormattedNotification extends Notification {
  channelLogo?: string | null;
  channelTitle?: string;
  eventTitle?: string;
  eventBanner?: string | null;
  // Announcement-specific fields
  isAnnouncement?: boolean;
  announcementID?: string;
  announcementImage?: string | null;
  creatorAvatar?: string | null;
}

// API returns isRead as "0"/"1" string, convert to boolean
const parseIsRead = (value: any): boolean => value === true || value === "1" || value === 1;

// Format Unix timestamp to relative time (e.g. "2h ago", "3d ago")
const formatTimeAgo = (timestamp: string | undefined): string => {
  if (!timestamp) return '';
  const seconds = Math.floor(Date.now() / 1000 - Number(timestamp));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ onClose, dropdownRef: externalRef, onUnreadCountChange }) => {
  const navigate = useNavigate();
  const { auth, language, setLoading, isLoading } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const [notifications, setNotifications] = useState<FormattedNotification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const isMountedRef = useRef(true);
  const internalDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRef = externalRef || internalDropdownRef;
  const hasFetchedRef = useRef(false);
  const initiallyUnreadIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Note: Click outside handling is done in Header component
  // to properly handle the notification button toggle

  const fetchNotifications = useCallback(async (_userId: string) => {
    if (!token) return;

    try {
      setLoading('notifications.fetch', true);
      setError(null);

      const response = await Services.GetUserNotifications(token);

      if (!isMountedRef.current) return;

      if (response.data.status && response.data.result) {
        const notificationsArray = response.data.result;

        // Format notifications using inline data from the API response
        const formattedNotifications = notificationsArray.map((notification: Notification): FormattedNotification => {
          const eventID = notification.data?.eventID || notification.eventID;
          const channelID = notification.data?.channelID || notification.channelID;
          const eventDetails = notification.data?.eventDetails;

          const formatted: FormattedNotification = {
            ...notification,
            isRead: parseIsRead(notification.isRead),
            eventID: eventID ? String(eventID) : notification.eventID,
            channelID: channelID ? String(channelID) : notification.channelID,
          };

          // Check if this is an announcement notification
          const announcementID = notification.data?.announcementID;
          if (announcementID) {
            formatted.isAnnouncement = true;
            formatted.announcementID = String(announcementID);
            formatted.creatorAvatar = notification.data?.createdBy?.userAvatar || null;
            formatted.announcementImage = notification.image || notification.data?.image || null;
            formatted.channelTitle = notification.data?.channelTitle || eventDetails?.channelTitle || '';
          } else if (eventDetails) {
            formatted.channelLogo = eventDetails.channelLogo || null;
            formatted.channelTitle = eventDetails.channelTitle || '';
            formatted.eventTitle = eventDetails.title || '';
            formatted.eventBanner = eventDetails.cover || notification.image || null;
          } else {
            // Fallback for older notifications without eventDetails
            formatted.eventBanner = notification.image || null;
          }

          return formatted;
        });

        // Filter out unlisted events (isPublic === "3")
        const visibleNotifications = formattedNotifications.filter((n: FormattedNotification) => {
          const eventDetails = n.data?.eventDetails;
          if (eventDetails && eventDetails.isPublic === "3") return false;
          return true;
        });

        if (isMountedRef.current) {
          // Capture which notifications were unread before marking all as read
          const unreadIds = visibleNotifications
            .filter(n => !n.isRead)
            .map(n => n.notificationID);
          initiallyUnreadIds.current = new Set(unreadIds);

          setNotifications(visibleNotifications);
          hasFetchedRef.current = true;

          // Mark all as read on the server and clear badge
          if (unreadIds.length > 0) {
            onUnreadCountChange?.(0);
            Services.SetUserNotificationsRead(token, true).catch(() => {});
          }
        }
      } else {
        setError(response.data.message || getTranslation('common.error'));
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err.message || getTranslation('common.somethingWentWrong'));
    } finally {
      if (isMountedRef.current) {
        setLoading('notifications.fetch', false);
        setInitialLoading(false);
      }
    }
  }, [token, getTranslation, setLoading, onUnreadCountChange]);

  // Load notifications on mount
  useEffect(() => {
    if (!token || !user?.id || hasFetchedRef.current) return;
    fetchNotifications(user.id);
  }, [token, user?.id, fetchNotifications]);

  const handleNotificationClick = async (e: React.MouseEvent, notification: FormattedNotification) => {
    e.preventDefault();
    e.stopPropagation();

    if (!token) return;

    // Get event/announcement info before closing dropdown (important for mobile)
    const eventID = notification.eventID || notification.data?.eventID;
    const eventTitle = notification.eventTitle || notification.data?.eventDetails?.title || notification.data?.eventTitle;
    const isAnnouncementNotif = notification.isAnnouncement;
    const announcementID = notification.announcementID;
    const channelID = notification.channelID || notification.data?.channelID;

    // Close dropdown immediately
    onClose();

    try {
      const isMobile = window.innerWidth <= 768;

      if (isAnnouncementNotif && announcementID && channelID) {
        // Navigate to channel detail page with announcement query param
        const channelUrl = generateCommunityShareUrl(String(channelID));
        const targetUrl = `${channelUrl}?announcement=${announcementID}`;
        if (isMobile) {
          setTimeout(() => navigate(targetUrl), 100);
        } else {
          navigate(targetUrl);
        }
      } else if (eventID) {
        // Navigate to event
        if (isMobile) {
          setTimeout(() => {
            navigate(generateEventUrl(eventTitle, String(eventID)));
          }, 100);
        } else {
          navigate(generateEventUrl(eventTitle, String(eventID)));
        }
      }
    } catch (err) {
      // Error handling notification click
    }
  };

  const loading = initialLoading || isLoading('notifications.fetch');

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.notificationsList}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.notificationItem} style={{ cursor: 'default' }}>
                <div className={styles.notificationContent}>
                  <div className={styles.channelLogoContainer}>
                    <LoadingSkeleton variant="circular" width={40} height={40} />
                  </div>
                  <div className={styles.notificationText}>
                    <LoadingSkeleton variant="text" width="70%" height={14} />
                    <div style={{ height: 6 }} />
                    <LoadingSkeleton variant="text" width="90%" height={14} />
                    <div style={{ height: 6 }} />
                    <LoadingSkeleton variant="text" width="40%" height={12} />
                  </div>
                  <div className={styles.eventBannerContainer}>
                    <LoadingSkeleton variant="rectangular" width={64} height={36} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className={styles.error}>
            {error}
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            {getTranslation('notifications.noNotifications')}
          </div>
        ) : (
          <div className={styles.notificationsList}>
            {notifications.map((notification) => (
              <button
                key={notification.notificationID}
                type="button"
                className={`${styles.notificationItem} ${initiallyUnreadIds.current.has(notification.notificationID) ? styles.unread : ''}`}
                onClick={(e) => handleNotificationClick(e, notification)}
              >
                <div className={styles.notificationContent}>
                  {/* Left: Avatar/Logo */}
                  <div className={styles.channelLogoContainer}>
                    {notification.isAnnouncement && notification.creatorAvatar ? (
                      <>
                        {imageLoading[`channel-${notification.notificationID}`] && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '50%' }} />}
                        <img
                          src={notification.creatorAvatar}
                          alt=""
                          className={styles.channelLogo}
                          onLoad={() => setImageLoading(prev => ({ ...prev, [`channel-${notification.notificationID}`]: false }))}
                          onError={() => {
                            setImageLoading(prev => ({ ...prev, [`channel-${notification.notificationID}`]: false }));
                          }}
                        />
                      </>
                    ) : notification.channelLogo ? (
                      <>
                        {imageLoading[`channel-${notification.notificationID}`] && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '50%' }} />}
                        <img
                          src={notification.channelLogo}
                          alt={notification.channelTitle || ''}
                          className={styles.channelLogo}
                          onLoad={() => setImageLoading(prev => ({ ...prev, [`channel-${notification.notificationID}`]: false }))}
                          onError={() => {
                            setImageLoading(prev => ({ ...prev, [`channel-${notification.notificationID}`]: false }));
                          }}
                        />
                      </>
                    ) : (
                      <ChannelInitialIcon
                        channelName={notification.channelTitle}
                        className={styles.channelLogo}
                      />
                    )}
                  </div>

                  {/* Center: Text */}
                  <div className={styles.notificationText}>
                    <div className={styles.notificationTitle}>
                      {notification.isAnnouncement
                        ? (notification.channelTitle
                          ? getTranslation('notifications.newAnnouncement', { channelTitle: notification.channelTitle })
                          : notification.title)
                        : (notification.channelTitle
                          ? getTranslation('notifications.createdNewEvent', { channelTitle: notification.channelTitle })
                          : notification.title)
                      }
                    </div>
                    <div className={styles.notificationDescription}>{notification.description}</div>
                    {notification.createdDateTime && (
                      <div className={styles.notificationDate}>{formatTimeAgo(notification.createdDateTime)}</div>
                    )}
                  </div>

                  {/* Right: Image */}
                  <div className={styles.eventBannerContainer}>
                    {notification.isAnnouncement ? (
                      notification.announcementImage ? (
                        <>
                          {imageLoading[`banner-${notification.notificationID}`] && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
                          <img
                            src={notification.announcementImage}
                            alt={notification.title || ''}
                            className={styles.eventBanner}
                            onLoad={() => setImageLoading(prev => ({ ...prev, [`banner-${notification.notificationID}`]: false }))}
                            onError={() => {
                              setImageLoading(prev => ({ ...prev, [`banner-${notification.notificationID}`]: false }));
                            }}
                          />
                        </>
                      ) : null
                    ) : notification.eventBanner ? (
                      <>
                        {imageLoading[`banner-${notification.notificationID}`] && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
                        <img
                          src={notification.eventBanner}
                          alt={notification.eventTitle || ''}
                          className={styles.eventBanner}
                          onLoad={() => setImageLoading(prev => ({ ...prev, [`banner-${notification.notificationID}`]: false }))}
                          onError={() => {
                            setImageLoading(prev => ({ ...prev, [`banner-${notification.notificationID}`]: false }));
                          }}
                        />
                      </>
                    ) : (
                      <GrayBanner className={styles.eventBanner} id={notification.eventID} />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsDropdown;
